/**
 * What stops a draft being saved, and where the trainer has to go to fix it.
 *
 * ── Four things block a save, and the backend agrees with three of them ─────────────────────
 * `name` and `primaryMuscle` are the product's rules: an unnamed movement is unfindable in a
 * library of five hundred, and one that trains nothing is absent from every muscle facet and from
 * `similar`. `metrics` is the product's rule too — an empty `measurementFields` is legal on the
 * wire (measurements R2) and is exactly right for a mobility movement, so it blocks everything
 * EXCEPT `mobility`, where „zaznacz wykonanie" is the whole prescription — and it never blocks an
 * EDIT, whose metrics are frozen and not the trainer's to fix.
 *
 * `links` is the backend's: `ExternalLinkPolicy` refuses the whole request on the first bad row
 * (library R12), so a draft carrying one would be a 400 the trainer meets after pressing save. The
 * entity's mirror is what lets them meet it while they are still typing, and a row that is simply
 * BLANK is not a refusal — it is a placeholder the submit drops.
 *
 * ── The order is the order the wizard is in ─────────────────────────────────────────────────
 * `blockedStepOf` reads the first blocker and names the step that fixes it, so a refused save sends
 * the trainer to the earliest thing that is wrong rather than to the last one the list happens to
 * mention. That is also why this returns a LIST rather than a first-refusal: the sentence names
 * everything missing at once („wpisz nazwę i zaznacz partię główną"), which is one trip instead of
 * three.
 *
 * ── Keys, never sentences ───────────────────────────────────────────────────────────────────
 * `model/` may not translate, and the sentence has a conjunction in it that only Polish can decide
 * („A, B i C"). So this answers what is missing and `ui/WizardFooter` composes the words.
 */

import { checkExerciseLinks, type LinkRefusal } from '~/entities/exercise-library';

import type { ExerciseDraft } from './exerciseDraft';
import { WIZARD_STEPS, type WizardStep } from './wizardStep';

/** One thing that is not yet true of the draft. */
export type DraftBlocker = 'name' | 'metrics' | 'primaryMuscle' | 'links';

/**
 * Whether this draft must measure something — the one statement of the rule, read by the blocker
 * below and by the „wymagane" beside the metrics label. Never on an edit (frozen, R11), never for
 * Mobilność („zaznacz wykonanie" is its whole prescription).
 */
export function metricsRequired(draft: ExerciseDraft): boolean {
  return !draft.metricsFrozen && draft.type !== 'mobility';
}

/** Everything standing between this draft and a save, in the wizard's own order. */
export function draftBlockers(draft: ExerciseDraft): readonly DraftBlocker[] {
  const blockers: DraftBlocker[] = [];
  if (draft.name.trim() === '') blockers.push('name');
  // Never on an edit: the rows are frozen and the PATCH cannot carry them (R11), so a movement that
  // measures nothing — legal (measurements R2), or relabelled from Mobilność — would be unsavable.
  if (metricsRequired(draft) && draft.metrics.fields.length === 0) blockers.push('metrics');
  if (draft.primary.length === 0) blockers.push('primaryMuscle');
  if (linkRowRefusal(draft.links) !== null) blockers.push('links');
  return blockers;
}

/** A link row the policy refuses, by the DRAFT's own index, so the sentence lands on that row. */
export interface LinkRowRefusal {
  readonly at: number;
  readonly refusal: LinkRefusal;
}

/**
 * The first link row the entity's policy refuses, or `null`.
 *
 * A blank row is a placeholder the submit drops, so it is never what refuses a save: only rows the
 * trainer has typed something into are put to the policy. The policy answers with an index into the
 * list it was HANDED, which is the typed rows alone — so the index is mapped back to the draft's own
 * row, or the sentence would land one row too high whenever a blank one sits above the bad one.
 */
export function linkRowRefusal(
  links: readonly { readonly url: string; readonly label: string }[],
): LinkRowRefusal | null {
  const typedAt = links.flatMap((link, index) =>
    link.url.trim() !== '' || link.label.trim() !== '' ? [index] : [],
  );
  const refused = checkExerciseLinks(typedAt.map((index) => links[index] ?? { url: '', label: '' }))
    .refused;
  if (refused === null) return null;
  return { at: typedAt[refused.at] ?? refused.at, refusal: refused.refusal };
}

/** Which step holds the control that fixes this. */
export function stepOfBlocker(blocker: DraftBlocker): WizardStep {
  switch (blocker) {
    case 'name':
    case 'metrics':
      return 'basics';
    case 'primaryMuscle':
      return 'body';
    case 'links':
      return 'materials';
  }
}

/**
 * The step a refused save sends the trainer to, or `null` when nothing is in the way.
 *
 * The FIRST blocker's step: the list is built in the wizard's own order, so the earliest thing that
 * is wrong is the one they are taken to.
 */
export function blockedStepOf(blockers: readonly DraftBlocker[]): WizardStep | null {
  const first = blockers[0];
  return first === undefined ? null : stepOfBlocker(first);
}

/**
 * What stops the trainer leaving THIS step forward — its own blockers and no other step's.
 *
 * Owner decision D19 (18 September 2026): „Dalej” does not let a trainer past a step that is not
 * done. Step 1 owns the name and the metrics rule, step 2 the primary muscle, step 3 only a refused
 * link row — the same four blockers the save knows, each asked on the step whose control fixes it.
 */
export function stepBlockers(draft: ExerciseDraft, step: WizardStep): readonly DraftBlocker[] {
  return draftBlockers(draft).filter((blocker) => stepOfBlocker(blocker) === step);
}

/**
 * Where a jump to `target` actually lands: `target` itself when every step before it is clear, or
 * the first step before it that is not — never past a blocker (D19). Going back is always allowed.
 */
export function reachableStep(draft: ExerciseDraft, target: WizardStep): WizardStep {
  const before = WIZARD_STEPS.slice(0, WIZARD_STEPS.indexOf(target));
  return before.find((step) => stepBlockers(draft, step).length > 0) ?? target;
}

/** What a press of save does: refuse and send the trainer to a field, wait for media, or go. */
export type SaveMove =
  | { readonly kind: 'refuse'; readonly step: WizardStep; readonly blocker: DraftBlocker }
  | { readonly kind: 'waitForMedia' }
  | { readonly kind: 'go' };

/** Which chosen files are still being prepared at the press. */
export interface MediaPreparing {
  readonly cover: boolean;
  readonly video: boolean;
}

/**
 * Whether a save may go now.
 *
 * A chosen picture is re-encoded before it becomes the draft's cover (D23), and a chosen film is
 * compressed before it becomes the draft's video — the picture takes a beat, the film can take a
 * minute. A save pressed inside either used to go out with what the draft had BEFORE the choice —
 * for a new movement nothing — and the file the trainer had just chosen was dropped without a word.
 * So a save pressed while EITHER is being prepared waits for it and sends what it produces.
 *
 * The draft's own blockers still come first: a nameless draft is refused whether or not a file is
 * on its way, because waiting would only delay the same refusal. A preparation that ends in a
 * refusal holds nothing: once it has settled, the next press goes, without that file.
 *
 * CHANGED DELIBERATELY, 25 September 2026: the wait was `waitForCover` and took one flag. The film
 * became a real control (owner decision, one video pipeline) and the wait is the same wait for
 * either file, so it is named for both.
 */
export function saveMove(draft: ExerciseDraft, preparing: MediaPreparing): SaveMove {
  const blockers = draftBlockers(draft);
  const first = blockers[0];
  const step = blockedStepOf(blockers);
  if (first !== undefined && step !== null) return { kind: 'refuse', step, blocker: first };
  return preparing.cover || preparing.video ? { kind: 'waitForMedia' } : { kind: 'go' };
}
